import { ColumnSort } from '@tanstack/react-table'

import { Company } from '@/types/dashboard-types'

import {
  getBasicPropColMeta,
  getBearingDirection,
  getDoorMpColMeta,
  getDynamicDoorMpColName,
  getDynamicTempMpColName,
  getFridgePropColMeta,
  getLocationColMeta,
  getTempMpColMeta,
  getVspColMeta,
  isDynamicDoorMpCol,
  isDynamicTempMpCol,
  isNullOrUndefined,
} from './utils'

import { AssetDoorMp, AssetStateSummary, AssetTempMp, Severity } from '../types'

type MetaData = { displayBlank: boolean; displayMissing: boolean }
type SortingFn = (a: AssetStateSummary, b: AssetStateSummary) => number

const getPriority = (meta: MetaData) => {
  if (meta.displayBlank) return 2
  if (meta.displayMissing) return 1
  return 0
}

const getAlertSeverityNumber = (severity: Severity | null): number => {
  switch (severity) {
    case 'critical':
      return 3
    case 'warning':
      return 2
    case 'informational':
      return 1
    default:
      return 0
  }
}

const getPowerStateNumber = (powerState: string | null, maintenanceMode: boolean): number => {
  switch (powerState) {
    case 'Reefer On':
      return maintenanceMode ? 3 : 4
    case 'Reefer Off':
      return maintenanceMode ? 1 : 2
    default:
      return 0
  }
}

const getGatewayOnNumber = (gatewayOn: boolean | null, isSleep: boolean | undefined): number => {
  return gatewayOn ? 2 : isSleep ? 1 : 0
}

const isEmpty = (v: unknown) => isNullOrUndefined(v) || (typeof v === 'string' && v.trim() === '')

const sortingFn =
  (
    getMeta: (
      a: AssetStateSummary,
      value: string | number | null | Date | AssetTempMp | AssetDoorMp | undefined
    ) => MetaData,
    getValue: (a: AssetStateSummary) => string | number | null | Date | AssetTempMp | AssetDoorMp | undefined,
    order: 'asc' | 'desc' | undefined,
    type: 'string' | 'numeric' | 'AssetTempMp' | 'AssetDoorMp' | 'date' = 'string'
  ): SortingFn =>
  (asset1, asset2) => {
    const v1 = getValue(asset1)
    const v2 = getValue(asset2)
    const m1 = getMeta(asset1, v1)
    const m2 = getMeta(asset2, v2)
    const p1 = getPriority(m1)
    const p2 = getPriority(m2)

    if (p1 !== p2) {
      return p1 - p2
    }
    if (p1 !== 0) {
      return 0
    }
    switch (type) {
      case 'numeric': {
        const r1 = Number(v1) - Number(v2)
        return order === 'desc' ? -r1 : r1
      }
      case 'AssetTempMp': {
        const r2 = Number((v1 as AssetTempMp).temperatureCelsius) - Number((v2 as AssetTempMp).temperatureCelsius)
        return order === 'desc' ? -r2 : r2
      }
      case 'AssetDoorMp': {
        const r3 = ((v1 as AssetDoorMp).connected ?? '').localeCompare((v2 as AssetDoorMp).connected ?? '')
        return order === 'desc' ? -r3 : r3
      }
      case 'date': {
        const r4 = (v1 as Date).getTime() - (v2 as Date).getTime()
        return order === 'desc' ? -r4 : r4
      }
      default: {
        const d = (v1?.toString() ?? '').localeCompare(v2?.toString() ?? '')
        return order === 'desc' ? -d : d
      }
    }
  }

const getColValue = (asset: AssetStateSummary, column: string): string | number | null => {
  return (asset as unknown as Record<string, string | number | null>)[column]
}

export const getColumnSortingFn = (
  column: string,
  order: 'desc' | 'asc' | undefined,
  company: Company | undefined
): SortingFn => {
  if (isDynamicTempMpCol(column)) {
    return sortingFn(
      (a, v) => getTempMpColMeta(a.gatewayOn, (v as AssetTempMp)?.tagOnline, (v as AssetTempMp)?.temperatureCelsius),
      (a) => a.tempMps.find((t) => getDynamicTempMpColName(t.name) === column),
      order,
      'AssetTempMp'
    )
  }

  if (isDynamicDoorMpCol(column)) {
    return sortingFn(
      (a, v) => getDoorMpColMeta(a.gatewayOn, (v as AssetDoorMp)?.tagOnline, (v as AssetDoorMp)?.connected),
      (a) => a.doorMps.find((t) => getDynamicDoorMpColName(t.name) === column),
      order,
      'AssetDoorMp'
    )
  }

  switch (column) {
    case 'alertSeverity':
      return sortingFn(
        (_, v) => ({ displayMissing: false, displayBlank: isNullOrUndefined(v) || (v as number) < 1 }),
        (a) => getAlertSeverityNumber(a.highestAlertSeverity),
        order,
        'numeric'
      )
    case 'powerOn':
      return sortingFn(
        (_, v) => ({ displayMissing: false, displayBlank: isNullOrUndefined(v) || (v as number) < 1 }),
        (a) => getPowerStateNumber(a.powerState, a.maintenanceMode),
        order,
        'numeric'
      )
    case 'gatewayOn':
      return sortingFn(
        (_, v) => ({ displayMissing: false, displayBlank: isNullOrUndefined(v) }),
        (a) => getGatewayOnNumber(a.gatewayOn, a.umdSleepMode),
        order,
        'numeric'
      )
    case 'addressShort':
    case 'street':
    case 'city':
    case 'postcode':
    case 'state':
    case 'country':
    case 'geofence':
      return sortingFn(
        (a, v) => getLocationColMeta(a.gatewayOn, a.locationSampleTime, v as string),
        (a) => getColValue(a, column) as string,
        order,
        'string'
      )
    case 'lastKnownAddress':
      return sortingFn(
        (_, v) => ({ displayMissing: false, displayBlank: isEmpty(v) }),
        (a) => a.addressShort ?? '',
        order,
        'string'
      )
    case 'lastKnownGeofence':
      return sortingFn(
        (_, v) => ({ displayMissing: false, displayBlank: isEmpty(v) }),
        (a) => a.geofence ?? '',
        order,
        'string'
      )
    case 'bearingDegrees':
      return sortingFn(
        (a, v) => getLocationColMeta(a.gatewayOn, a.locationSampleTime, v as string),
        (a) => getBearingDirection(getColValue(a, column) as number),
        order,
        'string'
      )
    case 'airControl':
    case 'operation':
    case 'powerMode':
    case 'powerSource':
    case 'fridgeProfile':
    case 'zone1Mode':
    case 'zone2Mode':
    case 'zone3Mode':
    case 'engineSpeed':
      return sortingFn(
        (a, v) => getFridgePropColMeta(a.powerState, a.fridgeOnline, v as string),
        (a) => getColValue(a, column) as string,
        order,
        'string'
      )
    case 'ambientTemperatureCelsius':
    case 'zone1SupplyAir':
    case 'zone1ReturnAir':
    case 'zone2SupplyAir':
    case 'zone2ReturnAir':
    case 'zone3SupplyAir':
    case 'zone3ReturnAir':
    case 'batteryVolts':
    case 'defrostInterval':
    case 'zone1SetPoint':
    case 'zone2SetPoint':
    case 'zone3SetPoint':
      return sortingFn(
        (a, v) => getFridgePropColMeta(a.powerState, a.fridgeOnline, v as string),
        (a) => getColValue(a, column) as number,
        order,
        'numeric'
      )
    case 'fuelPercent':
      return sortingFn(
        (a, v) => getBasicPropColMeta(a.gatewayOn, a.fridgeFuelOnline, v as string),
        (a) => getColValue(a, column) as number,
        order,
        'numeric'
      )
    case 'electricHours':
    case 'dieselHours':
    case 'engineHours':
    case 'totalHours':
    case 'compressorHours':
      return sortingFn(
        (a, v) => getBasicPropColMeta(a.gatewayOn, a.fridgeEngineHoursOnline, v as string),
        (a) => getColValue(a, column) as number,
        order,
        'numeric'
      )
    case 'speedKmsHr':
    case 'maxSpeedKmsHr':
      return sortingFn(
        (a, v) => getBasicPropColMeta(a.gatewayOn, a.locationOnline, v as string),
        (a) => getColValue(a, column) as number,
        order,
        'numeric'
      )
    case 'calibrationDays':
    case 'dieselHourstillnextservice':
      return sortingFn(
        (_, v) => ({ displayMissing: false, displayBlank: isNullOrUndefined(v) }),
        (a) => getColValue(a, column) as number,
        order,
        'numeric'
      )
    case 'despatchDate':
    case 'deliveryDate':
      return sortingFn(
        (_, v) => ({ displayMissing: false, displayBlank: isEmpty(v) }),
        (a) => getColValue(a, column) as string,
        order,
        'date'
      )
    case 'stopSampleTime':
      return sortingFn(
        (_, v) => ({ displayMissing: false, displayBlank: isEmpty(v) }),
        (a) => getColValue(a, column) as number,
        order,
        'numeric'
      )
    case 'offlineSince':
      return sortingFn(
        (a, _) => ({ displayMissing: false, displayBlank: a.gatewayOn }),
        (a) => getColValue(a, 'addressSampleTime') as string,
        order,
        'date'
      )
    case 'tempMpSummary':
      return sortingFn(
        (_, v) => ({ displayMissing: false, displayBlank: v === 0 }),
        (a) => a.tempMps.length,
        order,
        'numeric'
      )
    case 'doorMpSummary':
      return sortingFn(
        (_, v) => ({ displayMissing: false, displayBlank: v === 0 }),
        (a) => a.doorMps.length,
        order,
        'numeric'
      )
    case 'zone1VirtualSetpoint':
    case 'zone2VirtualSetpoint':
    case 'zone3VirtualSetpoint':
      return sortingFn(
        (a, v) => getVspColMeta(company?.virtualSetpointAccessTypeId, a.reeferInterfaceTypeId, v as number),
        (a) => getColValue(a, column) as string,
        order,
        'numeric'
      )
    default:
      return sortingFn(
        (_, v) => ({ displayBlank: isEmpty(v), displayMissing: false }),
        (a) => getColValue(a, column),
        order,
        'string'
      )
  }
}

export const sortAssets = (
  assets: AssetStateSummary[],
  sorting: ColumnSort[],
  company: Company | undefined
): AssetStateSummary[] => {
  if (!sorting.length) {
    return assets
  }
  const column = sorting[0].id.split('-').pop()
  const order = sorting[0].desc === true ? 'desc' : sorting[0].desc === false ? 'asc' : undefined
  if (!column) {
    return assets
  }
  return [...assets].sort(getColumnSortingFn(column, order, company))
}
